import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Save, X, Mail, Loader, Download, Upload, Tag, Eye, Copy, CheckCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { apiClient } from '../../config/api';
import UnlayerEditor from '../../components/UnlayerEditor';
import Modal from '../../components/Modal';

const AdminMessageTemplatesPage = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [importedTemplates, setImportedTemplates] = useState([]); // VoiceBox imported templates
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [selectedType, setSelectedType] = useState('sales');
    const [activeTab, setActiveTab] = useState('templates'); // 'templates' or 'imported'
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [editingImportedTemplate, setEditingImportedTemplate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [groups, setGroups] = useState([]);
    const [brands, setBrands] = useState([]);
    const [availableFields, setAvailableFields] = useState([]);
    const [dealershipTags, setDealershipTags] = useState([]);
    const [extractedTags, setExtractedTags] = useState([]);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
    const [selectedImportedIds, setSelectedImportedIds] = useState(new Set());
    const [deletingTemplateId, setDeletingTemplateId] = useState(null);
    const [deletingAll, setDeletingAll] = useState(false);
    
    // Template form state
    const [templateForm, setTemplateForm] = useState({
        message_name: '',
        subject: '',
        from_name: '',
        from_address: '',
        notes: '',
        message_type: 'sales',
        html_content: '',
        unlayer_json: null
    });

    useEffect(() => {
        loadTemplates();
        loadImportedTemplates();
        loadAvailableFields();
        loadDealershipTags();
        loadGroups();
        loadBrands();
    }, [selectedType]);

    const loadAvailableFields = async () => {
        try {
            const result = await apiClient.get('/workflows/variable-mapping-fields');
            // Transform fields into format for UnLayer merge tags (customer wildcards)
            const fields = (result.fields || []).map(field => ({
                name: field.label || field.path,
                tag: field.path?.split('.').pop() || field.name,
                value: `%%${field.path?.split('.').pop() || field.name}%%`,
                category: 'customer' // Mark as customer wildcards
            }));
            setAvailableFields(fields);
        } catch (error) {
            console.error('Failed to load available fields:', error);
            setAvailableFields([]);
        }
    };

    const loadDealershipTags = async () => {
        try {
            const result = await apiClient.get('/dealership-tags');
            // Dealership tags from fact find (dealership/location info)
            const tags = (result.tags || []).map(tag => ({
                name: tag.label || tag.tag,
                tag: tag.tag,
                value: tag.value || `%%${tag.tag}%%`,
                category: tag.category || 'dealership',
                field_key: tag.field_key
            }));
            setDealershipTags(tags);
        } catch (error) {
            console.error('Failed to load dealership tags:', error);
            setDealershipTags([]);
        }
    };

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const result = await apiClient.get('/contact-messages', { type: selectedType });
            // Filter out brand templates (those are managed separately)
            const regularTemplates = (result || []).filter(t => !t.is_brand_template);
            setTemplates(regularTemplates);
        } catch (error) {
            console.error('Failed to load templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadImportedTemplates = async () => {
        try {
            const result = await apiClient.get('/voicebox/unlayer-templates?converted=false');
            setImportedTemplates(result.templates || []);
        } catch (error) {
            console.error('Failed to load imported templates:', error);
        }
    };

    const loadGroups = async () => {
        try {
            const result = await apiClient.get('/groups');
            setGroups(result || []);
            if (result && result.length > 0 && !selectedGroupId) {
                setSelectedGroupId(result[0].id);
            }
        } catch (error) {
            console.error('Failed to load groups:', error);
        }
    };

    const loadBrands = async () => {
        try {
            const result = await apiClient.get('/brands');
            setBrands(result || []);
        } catch (error) {
            console.error('Failed to load brands:', error);
        }
    };

    const handleImportUnlayerTemplates = async () => {
        if (!selectedGroupId) {
            alert('Please select a dealership group first');
            return;
        }

        setImporting(true);
        try {
            const result = await apiClient.post(`/voicebox/groups/${selectedGroupId}/import-unlayer-templates`, {
                overwrite: false
            }, {}, 600000); // 10-minute timeout
            alert(`Imported ${result.imported} template(s). ${result.skipped} skipped.`);
            loadImportedTemplates();
        } catch (error) {
            console.error('Failed to import templates:', error);
            alert('Failed to import templates: ' + (error.message || 'Unknown error'));
        } finally {
            setImporting(false);
        }
    };

    const handleCreateNew = () => {
        setEditingTemplate(null);
        setTemplateForm({
            message_name: '',
            subject: '',
            from_name: '',
            from_address: '',
            notes: '',
            message_type: selectedType,
            html_content: '',
            unlayer_json: null
        });
        setIsEditorOpen(true);
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setEditingImportedTemplate(null);
        setTemplateForm({
            message_name: template.message_name || '',
            subject: template.subject || '',
            from_name: template.from_name || '',
            from_address: template.from_address || '',
            notes: template.notes || '',
            message_type: template.message_type?.toLowerCase() || selectedType,
            html_content: template.html_content || '',
            unlayer_json: template.unlayer_json || null
        });
        setIsEditorOpen(true);
    };

    const handleEditImportedTemplate = async (template) => {
        try {
            // Fetch full template with unlayer_json
            const fullTemplate = await apiClient.get(`/voicebox/unlayer-templates/${template.id}`);
            
            // Log to verify we're receiving unlayer_json from the database column
            console.log('Template data received from voicebox_unlayer_templates:', {
                id: fullTemplate.id,
                message_name: fullTemplate.message_name,
                has_html_content: !!fullTemplate.html_content,
                html_content_length: fullTemplate.html_content ? fullTemplate.html_content.length : 0,
                has_unlayer_json: !!fullTemplate.unlayer_json,
                unlayer_json_type: typeof fullTemplate.unlayer_json,
                unlayer_json_keys: fullTemplate.unlayer_json && typeof fullTemplate.unlayer_json === 'object' 
                    ? Object.keys(fullTemplate.unlayer_json) 
                    : 'N/A',
                note: 'Using unlayer_json column from voicebox_unlayer_templates table'
            });
            
            setEditingImportedTemplate(fullTemplate);
            setEditingTemplate(null);
            
            // Use unlayer_json from the database column (already parsed by backend if needed)
            // The backend ensures it's a proper object or null
            let parsedUnlayerJson = fullTemplate.unlayer_json;
            
            // Additional safety check - parse if still a string (shouldn't happen after backend fix, but just in case)
            if (typeof parsedUnlayerJson === 'string') {
                try {
                    parsedUnlayerJson = JSON.parse(parsedUnlayerJson);
                    console.log('Parsed unlayer_json from string (backend should have handled this)');
                } catch (parseError) {
                    console.error('Failed to parse unlayer_json:', parseError);
                    parsedUnlayerJson = null;
                }
            }
            
            // Ensure it's a valid object (not an array or primitive)
            if (parsedUnlayerJson !== null && typeof parsedUnlayerJson !== 'object') {
                console.warn('unlayer_json is not an object:', typeof parsedUnlayerJson);
                parsedUnlayerJson = null;
            }
            
            setTemplateForm({
                message_name: fullTemplate.message_name || '',
                subject: fullTemplate.subject || '',
                from_name: fullTemplate.from_name || '',
                from_address: fullTemplate.from_address || '',
                notes: '',
                message_type: 'sales',
                html_content: fullTemplate.html_content || '', // HTML fallback if unlayer_json is not available
                unlayer_json: parsedUnlayerJson // Primary source: unlayer_json column from voicebox_unlayer_templates
            });
            
            // Log final state for debugging
            console.log('Template form set with:', {
                has_unlayer_json: !!parsedUnlayerJson,
                unlayer_json_type: typeof parsedUnlayerJson,
                unlayer_json_top_keys: parsedUnlayerJson ? Object.keys(parsedUnlayerJson) : 'null',
                unlayer_json_has_body: !!parsedUnlayerJson?.body,
                unlayer_json_body_type: typeof parsedUnlayerJson?.body,
                unlayer_json_body_rows: Array.isArray(parsedUnlayerJson?.body?.rows) ? parsedUnlayerJson.body.rows.length : 'N/A',
                unlayer_json_preview: JSON.stringify(parsedUnlayerJson)?.substring(0, 500),
                has_html_content: !!fullTemplate.html_content,
                will_use_editor: !!parsedUnlayerJson,
                will_use_html_fallback: !parsedUnlayerJson && !!fullTemplate.html_content
            });
            setIsEditorOpen(true);
        } catch (error) {
            console.error('Failed to load imported template:', error);
            alert('Failed to load template: ' + (error.message || 'Unknown error'));
        }
    };

    const handleSaveImportedTemplate = async () => {
        if (!editingImportedTemplate) return;

        setSaving(true);
        try {
            const payload = {
                message_name: templateForm.message_name,
                subject: templateForm.subject,
                from_name: templateForm.from_name,
                from_address: templateForm.from_address,
                html_content: templateForm.html_content,
                unlayer_json: templateForm.unlayer_json
            };

            await apiClient.put(`/voicebox/unlayer-templates/${editingImportedTemplate.id}`, payload);
            setIsEditorOpen(false);
            loadImportedTemplates();
            alert('Template saved successfully!');
        } catch (error) {
            console.error('Failed to save template:', error);
            alert('Failed to save template: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleSyncToVoicebox = async () => {
        if (!editingImportedTemplate) return;

        // Show detailed warning about VoiceBox update
        const confirmMessage = `⚠️ WARNING: This will UPDATE the VoiceBox database\n\n` +
            `You are about to sync this template back to VoiceBox.\n\n` +
            `This will:\n` +
            `- Update the VoiceBox Messages table\n` +
            `- Modify message ${editingImportedTemplate.voicebox_message_id || 'N/A'}\n` +
            `- Update EmailContentJson, HtmlContent, Subject, FromName, FromAddress\n\n` +
            `⚠️ Changes cannot be easily undone.\n\n` +
            `Type "SYNC" to confirm:`;
        
        const userInput = window.prompt(confirmMessage);
        if (userInput !== 'SYNC') {
            return;
        }

        setSaving(true);
        try {
            const result = await apiClient.post(`/voicebox/unlayer-templates/${editingImportedTemplate.id}/sync-to-voicebox`, {
                overwrite: true,
                confirm_sync: true
            });
            
            alert(`${result.message || 'Template synced to VoiceBox successfully!'}\n\n${result.warning || ''}`);
            // Optionally reload templates
            loadImportedTemplates();
        } catch (error) {
            console.error('Failed to sync template to VoiceBox:', error);
            const errorMsg = error.message || 'Unknown error';
            if (errorMsg.includes('Confirmation required')) {
                alert('⚠️ Confirmation required: ' + errorMsg);
            } else {
                alert('Failed to sync template to VoiceBox: ' + errorMsg);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleConvertToBrand = async () => {
        if (!editingImportedTemplate) return;
        
        const brandId = window.prompt('Enter Brand ID to convert this template to:');
        if (!brandId) return;

        setSaving(true);
        try {
            const payload = {
                brand_id: brandId,
                message_name: templateForm.message_name,
                subject: templateForm.subject,
                from_name: templateForm.from_name,
                from_address: templateForm.from_address,
                html_content: templateForm.html_content,
                unlayer_json: templateForm.unlayer_json,
                message_type: templateForm.message_type
            };

            await apiClient.post(`/voicebox/unlayer-templates/${editingImportedTemplate.id}/convert-to-brand`, payload);
            setIsEditorOpen(false);
            loadImportedTemplates();
            alert('Template converted to brand template successfully!');
        } catch (error) {
            console.error('Failed to convert template:', error);
            alert('Failed to convert template: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleUnlayerSave = ({ html, json, tags }) => {
        setTemplateForm(prev => ({
            ...prev,
            html_content: html,
            unlayer_json: json
        }));
        if (tags && tags.length > 0) {
            setExtractedTags(tags);
        }
    };

    const handleTagsExtracted = (tags) => {
        setExtractedTags(tags);
    };

    const handleSaveTemplate = async () => {
        setSaving(true);
        try {
            const payload = {
                ...templateForm,
                message_content: templateForm.html_content || templateForm.message_name, // Fallback for plain text
                extracted_tags: extractedTags // Include extracted tags
            };

            if (editingTemplate) {
                await apiClient.put(`/contact-messages/${editingTemplate.id}`, payload);
            } else {
                await apiClient.post('/contact-messages', payload);
            }

            setIsEditorOpen(false);
            loadTemplates();
        } catch (error) {
            console.error('Failed to save template:', error);
            alert('Failed to save template: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm(`Are you sure you want to delete "${template.message_name}"? This action cannot be undone.`)) {
            return;
        }

        setDeletingTemplateId(template.id);
        try {
            await apiClient.delete(`/contact-messages/${template.id}`);
            loadTemplates();
            // Remove from selection if it was selected
            setSelectedTemplateIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(template.id);
                return newSet;
            });
        } catch (error) {
            console.error('Failed to delete template:', error);
            alert('Failed to delete template: ' + (error.message || 'Unknown error'));
        } finally {
            setDeletingTemplateId(null);
        }
    };

    const handleDeleteImportedTemplate = async (template) => {
        if (!window.confirm(`Are you sure you want to delete "${template.message_name}"? This will permanently remove it from imported templates.`)) {
            return;
        }

        setDeletingTemplateId(template.id);
        try {
            await apiClient.delete(`/voicebox/unlayer-templates/${template.id}`);
            loadImportedTemplates();
            // Remove from selection if it was selected
            setSelectedImportedIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(template.id);
                return newSet;
            });
        } catch (error) {
            console.error('Failed to delete imported template:', error);
            alert('Failed to delete template: ' + (error.response?.data?.error || error.message || 'Unknown error'));
        } finally {
            setDeletingTemplateId(null);
        }
    };

    const handleToggleSelect = (id, isImported = false) => {
        if (isImported) {
            setSelectedImportedIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(id)) {
                    newSet.delete(id);
                } else {
                    newSet.add(id);
                }
                return newSet;
            });
        } else {
            setSelectedTemplateIds(prev => {
                const newSet = new Set(prev);
                if (newSet.has(id)) {
                    newSet.delete(id);
                } else {
                    newSet.add(id);
                }
                return newSet;
            });
        }
    };

    const handleSelectAll = (isImported = false) => {
        if (isImported) {
            if (selectedImportedIds.size === importedTemplates.length) {
                setSelectedImportedIds(new Set());
            } else {
                setSelectedImportedIds(new Set(importedTemplates.map(t => t.id)));
            }
        } else {
            if (selectedTemplateIds.size === templates.length) {
                setSelectedTemplateIds(new Set());
            } else {
                setSelectedTemplateIds(new Set(templates.map(t => t.id)));
            }
        }
    };

    const handleBulkDelete = async (isImported = false) => {
        const selectedIds = isImported ? Array.from(selectedImportedIds) : Array.from(selectedTemplateIds);
        
        if (selectedIds.length === 0) {
            alert('Please select at least one template to delete.');
            return;
        }

        const confirmMessage = isImported
            ? `Are you sure you want to delete ${selectedIds.length} imported template(s)? This action cannot be undone.`
            : `Are you sure you want to delete ${selectedIds.length} template(s)? This action cannot be undone.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingAll(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Delete all selected templates
            await Promise.all(
                selectedIds.map(async (id) => {
                    try {
                        if (isImported) {
                            await apiClient.delete(`/voicebox/unlayer-templates/${id}`);
                        } else {
                            await apiClient.delete(`/contact-messages/${id}`);
                        }
                        successCount++;
                    } catch (error) {
                        console.error(`Failed to delete template ${id}:`, error);
                        failCount++;
                    }
                })
            );

            if (failCount > 0) {
                alert(`Deleted ${successCount} template(s) successfully. ${failCount} template(s) failed to delete.`);
            } else {
                alert(`Successfully deleted ${successCount} template(s).`);
            }

            // Reload templates
            if (isImported) {
                loadImportedTemplates();
                setSelectedImportedIds(new Set());
            } else {
                loadTemplates();
                setSelectedTemplateIds(new Set());
            }
        } catch (error) {
            console.error('Error during bulk delete:', error);
            alert('An error occurred during bulk delete. Some templates may have been deleted.');
        } finally {
            setDeletingAll(false);
        }
    };

    const handleDeleteAll = async (isImported = false) => {
        const templatesToDelete = isImported ? importedTemplates : templates;
        
        if (templatesToDelete.length === 0) {
            alert('No templates to delete.');
            return;
        }

        const confirmMessage = isImported
            ? `⚠️ WARNING: This will DELETE ALL ${templatesToDelete.length} imported template(s)!\n\nThis action cannot be undone.\n\nType "DELETE ALL" to confirm:`
            : `⚠️ WARNING: This will DELETE ALL ${templatesToDelete.length} template(s)!\n\nThis action cannot be undone.\n\nType "DELETE ALL" to confirm:`;

        const userInput = window.prompt(confirmMessage);
        if (userInput !== 'DELETE ALL') {
            return;
        }

        setDeletingAll(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Delete all templates
            await Promise.all(
                templatesToDelete.map(async (template) => {
                    try {
                        if (isImported) {
                            await apiClient.delete(`/voicebox/unlayer-templates/${template.id}`);
                        } else {
                            await apiClient.delete(`/contact-messages/${template.id}`);
                        }
                        successCount++;
                    } catch (error) {
                        console.error(`Failed to delete template ${template.id}:`, error);
                        failCount++;
                    }
                })
            );

            if (failCount > 0) {
                alert(`Deleted ${successCount} template(s) successfully. ${failCount} template(s) failed to delete.`);
            } else {
                alert(`Successfully deleted all ${successCount} template(s).`);
            }

            // Reload templates
            if (isImported) {
                loadImportedTemplates();
                setSelectedImportedIds(new Set());
            } else {
                loadTemplates();
                setSelectedTemplateIds(new Set());
            }
        } catch (error) {
            console.error('Error during delete all:', error);
            alert('An error occurred during delete all. Some templates may have been deleted.');
        } finally {
            setDeletingAll(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Mail size={32} />
                        Message Templates
                    </h1>
                    <p className="text-gray-600">
                        Create and manage email message templates using UnLayer visual editor, similar to VoiceBox.
                    </p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus size={18} />
                    New Template
                </button>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px">
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 ${
                                activeTab === 'templates'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Templates
                        </button>
                        <button
                            onClick={() => setActiveTab('imported')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 ${
                                activeTab === 'imported'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Imported from VoiceBox
                        </button>
                    </nav>
                </div>
            </div>

            {/* Type Filter - only show for templates tab */}
            {activeTab === 'templates' && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setSelectedType('sales')}
                            className={`px-4 py-2 rounded-md ${
                                selectedType === 'sales'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Sales Templates
                        </button>
                        <button
                            onClick={() => setSelectedType('service')}
                            className={`px-4 py-2 rounded-md ${
                                selectedType === 'service'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Service Templates
                        </button>
                    </div>
                </div>
            )}

            {/* Import Section - only show for imported tab */}
            {activeTab === 'imported' && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Import VoiceBox Templates</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Import UnLayer templates from VoiceBox to edit and convert to brand templates.
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <select
                                    value={selectedGroupId || ''}
                                    onChange={(e) => setSelectedGroupId(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select Group...</option>
                                    {groups.map(group => {
                                        const isSynced = group.is_voicebox_synced === true;
                                        return (
                                            <option key={group.id} value={group.id}>
                                                {group.name}{!isSynced && ' (Not Synced)'}
                                            </option>
                                        );
                                    })}
                                </select>
                                {selectedGroupId && (() => {
                                    const selectedGroup = groups.find(g => g.id === selectedGroupId);
                                    const isSynced = selectedGroup?.is_voicebox_synced === true;
                                    return isSynced ? (
                                        <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <CheckCircle2 size={18} className="text-green-600" />
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            <button
                                onClick={handleImportUnlayerTemplates}
                                disabled={!selectedGroupId || importing}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
                            >
                                {importing ? (
                                    <>
                                        <Loader className="animate-spin" size={16} />
                                        Importing...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} />
                                        Import Templates
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Templates List - Templates Tab */}
            {activeTab === 'templates' && (loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Loader className="animate-spin mx-auto" size={32} />
                    <p className="text-gray-600 mt-4">Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Mail className="mx-auto text-gray-400" size={48} />
                    <p className="text-gray-600 mt-4">No templates found. Create your first template to get started.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    {/* Bulk Actions Bar */}
                    {(selectedTemplateIds.size > 0 || templates.length > 0) && (
                        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <span className="text-sm text-gray-700">
                                    {selectedTemplateIds.size > 0 
                                        ? `${selectedTemplateIds.size} template(s) selected`
                                        : `${templates.length} template(s) total`}
                                </span>
                                {selectedTemplateIds.size > 0 && (
                                    <button
                                        onClick={() => handleBulkDelete(false)}
                                        disabled={deletingAll}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 text-sm"
                                    >
                                        {deletingAll ? (
                                            <>
                                                <Loader className="animate-spin" size={16} />
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 size={16} />
                                                Delete Selected ({selectedTemplateIds.size})
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            {templates.length > 0 && (
                                <button
                                    onClick={() => handleDeleteAll(false)}
                                    disabled={deletingAll}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-300 text-sm"
                                >
                                    {deletingAll ? (
                                        <>
                                            <Loader className="animate-spin" size={16} />
                                            Deleting All...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} />
                                            Delete All
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                                    <input
                                        type="checkbox"
                                        checked={templates.length > 0 && selectedTemplateIds.size === templates.length}
                                        onChange={() => handleSelectAll(false)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Has UnLayer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {templates.map((template) => (
                                <tr key={template.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedTemplateIds.has(template.id)}
                                            onChange={() => handleToggleSelect(template.id, false)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">{template.message_name}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{template.subject || '-'}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {template.from_name && template.from_address
                                            ? `${template.from_name} <${template.from_address}>`
                                            : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                                        {template.message_type?.toLowerCase() || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        {template.unlayer_json ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Yes</span>
                                        ) : (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">No</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEdit(template)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Edit"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(template)}
                                                disabled={deletingTemplateId === template.id}
                                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                                title="Delete"
                                            >
                                                {deletingTemplateId === template.id ? (
                                                    <Loader className="animate-spin" size={18} />
                                                ) : (
                                                    <Trash2 size={18} />
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}

            {/* Imported Templates List - Imported Tab */}
            {activeTab === 'imported' && (
                importedTemplates.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                        <Download className="mx-auto text-gray-400" size={48} />
                        <p className="text-gray-600 mt-4">No imported templates found. Import templates from VoiceBox to get started.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                        {/* Bulk Actions Bar */}
                        {(selectedImportedIds.size > 0 || importedTemplates.length > 0) && (
                            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-gray-700">
                                        {selectedImportedIds.size > 0 
                                            ? `${selectedImportedIds.size} template(s) selected`
                                            : `${importedTemplates.length} template(s) total`}
                                    </span>
                                    {selectedImportedIds.size > 0 && (
                                        <button
                                            onClick={() => handleBulkDelete(true)}
                                            disabled={deletingAll}
                                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 text-sm"
                                        >
                                            {deletingAll ? (
                                                <>
                                                    <Loader className="animate-spin" size={16} />
                                                    Deleting...
                                                </>
                                            ) : (
                                                <>
                                                    <Trash2 size={16} />
                                                    Delete Selected ({selectedImportedIds.size})
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                                {importedTemplates.length > 0 && (
                                    <button
                                        onClick={() => handleDeleteAll(true)}
                                        disabled={deletingAll}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-300 text-sm"
                                    >
                                        {deletingAll ? (
                                            <>
                                                <Loader className="animate-spin" size={16} />
                                                Deleting All...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 size={16} />
                                                Delete All
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                                        <input
                                            type="checkbox"
                                            checked={importedTemplates.length > 0 && selectedImportedIds.size === importedTemplates.length}
                                            onChange={() => handleSelectAll(true)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folder</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {importedTemplates.map((template) => (
                                    <tr key={template.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedImportedIds.has(template.id)}
                                                onChange={() => handleToggleSelect(template.id, true)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">{template.message_name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{template.subject || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{template.brand_name || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{template.folder_path || '-'}</td>
                                        <td className="px-6 py-4 text-sm">
                                            {template.available_tags && Array.isArray(template.available_tags) && template.available_tags.length > 0 ? (
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                    {template.available_tags.length} tag(s)
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {template.converted_to_brand_template ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center gap-1">
                                                    <CheckCircle size={12} />
                                                    Converted
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Pending</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEditImportedTemplate(template)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Edit & Convert"
                                                >
                                                    <Edit2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteImportedTemplate(template)}
                                                    disabled={deletingTemplateId === template.id}
                                                    className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                                    title="Delete"
                                                >
                                                    {deletingTemplateId === template.id ? (
                                                        <Loader className="animate-spin" size={18} />
                                                    ) : (
                                                        <Trash2 size={18} />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {/* UnLayer Editor Modal */}
            {isEditorOpen && (
                <Modal
                    isOpen={isEditorOpen}
                    onClose={() => setIsEditorOpen(false)}
                    title={editingImportedTemplate 
                        ? 'Edit & Convert Imported Template' 
                        : editingTemplate 
                            ? 'Edit Message Template' 
                            : 'Create Message Template'}
                    size="2xl"
                    resizable={true}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Template Name *
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.message_name}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, message_name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Welcome Email"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Subject Line
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.subject}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Welcome to our dealership!"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Name
                                </label>
                                <input
                                    type="text"
                                    value={templateForm.from_name}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, from_name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="Dealership Name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Email
                                </label>
                                <input
                                    type="email"
                                    value={templateForm.from_address}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, from_address: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="noreply@dealership.com"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes
                            </label>
                            <textarea
                                value={templateForm.notes}
                                onChange={(e) => setTemplateForm(prev => ({ ...prev, notes: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows={2}
                                placeholder="Optional notes about this template"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Content (UnLayer Editor)
                            </label>
                            
                            <UnlayerEditor
                                design={templateForm.unlayer_json}
                                htmlContent={templateForm.html_content}
                                onSave={handleUnlayerSave}
                                onTagsExtracted={handleTagsExtracted}
                                displayMode="email"
                                editorId="message-template-editor"
                                showSaveButton={false}
                                availableFields={[...dealershipTags, ...availableFields]}
                                mergeTags={dealershipTags.map(tag => ({
                                    name: tag.name,
                                    value: tag.value
                                }))}
                            />
                            {extractedTags.length > 0 && (
                                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <p className="text-xs font-medium text-gray-900 mb-1">Detected Tags in Template:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {extractedTags.map((tag, idx) => {
                                            const isDealership = dealershipTags.some(dt => dt.tag === tag);
                                            return (
                                                <span
                                                    key={idx}
                                                    className={`px-2 py-0.5 rounded text-xs font-mono ${
                                                        isDealership 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-blue-100 text-blue-800'
                                                    }`}
                                                    title={isDealership ? 'Dealership tag' : 'Customer wildcard'}
                                                >
                                                    %%{tag}%%
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <button
                                onClick={() => setIsEditorOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            {editingImportedTemplate ? (
                                <>
                                    <button
                                        onClick={handleSaveImportedTemplate}
                                        disabled={saving || !templateForm.message_name}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader className="animate-spin" size={16} />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                Save Template
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleSyncToVoicebox}
                                        disabled={saving || !templateForm.message_name}
                                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader className="animate-spin" size={16} />
                                                Syncing...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={16} />
                                                Sync to VoiceBox
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleConvertToBrand}
                                        disabled={saving || !templateForm.message_name}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader className="animate-spin" size={16} />
                                                Converting...
                                            </>
                                        ) : (
                                            <>
                                                <Tag size={16} />
                                                Convert to Brand Template
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleSaveTemplate}
                                    disabled={saving || !templateForm.message_name}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                                >
                                    {saving ? (
                                        <>
                                            <Loader className="animate-spin" size={16} />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} />
                                            Save Template
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminMessageTemplatesPage;
